import React, { useEffect, useState } from 'react';
import { QrCode, LinkSimple, Spinner } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { copyTextToClipboard } from '../../lib/clipboard';

// Small dialog that surfaces the freshly-created share URL alongside its QR
// code. Built for in-person estimator walkthroughs where the homeowner scans
// the QR on their phone to instantly see the 5-year time-lapse of their
// own property.
//
// Rendered as a Radix dialog so it overlays cleanly on top of the
// historical-compare overlay (z-50 > z-40). Keyboard ESC + click-outside both
// dismiss via Radix defaults.
//
// Props
//   share        { url, isBuilding, copied } | null   — null hides the dialog
//   projectName  string?                              — used in the description copy
//   onClose      () => void                           — called on any dismiss
export function ShareQrModal({ share, projectName, onClose }) {
  const [recopied, setRecopied] = useState(false);
  useEffect(() => { if (!share) setRecopied(false); }, [share]);
  if (!share) return null;
  const { url, isBuilding } = share;
  const copyAgain = async () => {
    const ok = await copyTextToClipboard(url);
    if (ok) {
      setRecopied(true);
      setTimeout(() => setRecopied(false), 1800);
      toast.success('Link copied');
    } else {
      toast.error('Clipboard blocked — select the URL manually to copy.');
    }
  };
  return (
    <Dialog open={!!share} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="share-qr-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode size={18} weight="duotone" className="text-primary" />
            Share this time-lapse
          </DialogTitle>
          <DialogDescription className="text-xs">
            {projectName
              ? `Scan the QR with a phone to open the time-lapse of "${projectName}", or copy the link below.`
              : 'Scan the QR with a phone to open this time-lapse, or copy the link below.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 mt-2">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-border" data-testid="share-qr-canvas">
            {/* qrcode.react renders a clean SVG — sharp at any zoom level so
                phones at arm's length on a sunny site still scan reliably. */}
            <QRCodeSVG
              value={url}
              size={208}
              level="M"
              marginSize={1}
              fgColor="#0F1117"
              bgColor="#FFFFFF"
            />
          </div>

          <div className="w-full">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-60">Public link · expires in 7 days</span>
              {isBuilding && (
                <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center gap-1">
                  <Spinner size={10} className="animate-spin" /> rendering
                </span>
              )}
            </div>
            <div
              className="w-full font-mono text-[11px] px-2.5 py-2 rounded bg-secondary/60 break-all border border-border select-all"
              data-testid="share-qr-url"
            >
              {url}
            </div>
          </div>

          <button
            type="button"
            onClick={copyAgain}
            className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-colors ${recopied ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground hover:opacity-90'}`}
            data-testid="share-qr-copy-btn"
          >
            <LinkSimple size={13} weight="bold" />
            {recopied ? 'Copied!' : 'Copy link again'}
          </button>

          <p className="text-[10px] text-center opacity-60 -mt-1 leading-relaxed">
            {isBuilding
              ? 'Recipients opening the link before the build finishes see a friendly "preparing video" page that auto-refreshes — typically takes 30–60 seconds.'
              : 'Recipients open the link to watch the MP4 inline (Slack, Gmail, browsers — no app required).'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
